/**
 * MaterialsTab.jsx - Tracking > Materials: Equipment & Materials (the tracking master's
 * Equipment&Materials sheet): one line per purchase - date, the PO line it is
 * billed to, PO status, vendor, task (Equipment, Shipping, Spares ...),
 * description, quantity, unit sell and unit cost, the vendor's invoice # and
 * amount. Sold = quantity x unit sell, cost = quantity x unit cost; both go
 * into AS SOLD and AS SOLD COST on the Summary. Projects Edit adds and
 * changes lines; the margin is the cost area's.
 */
import { useState } from 'react';
import { Btn, Field, GREEN, MUTED, NumInput, Section, SelectInput, TextInput, td, th } from '../quotes/quoteUi';
import { addMaterial, deleteMaterial, money, pct } from '../costs/costApi';
import { Empty, Figure, Num } from './trackingUi';

const blank = () => ({
  date: '',
  poLine: '',
  poStatus: '',
  vendor: '',
  task: 'Equipment',
  description: '',
  qty: '',
  unitSell: '',
  unitCost: '',
  vendorInvoiceNo: '',
  vendorInvoiceAmount: '',
});

export default function MaterialsTab({ data, projectId, editable, seesCosts, reload }) {
  const t = data.tracking;
  const lines = t.materials || [];
  const tasks = (data.lists && data.lists.tasks) || [];
  const [row, setRow] = useState(blank);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setRow((r) => ({ ...r, [k]: v }));
  const add = async () => {
    setBusy(true);
    const r = await addMaterial(projectId, row);
    setBusy(false);
    if (!r.ok) {
      setMsg({ kind: 'error', text: (r.body && r.body.error) || 'Could not add the line.' });
      return;
    }
    setMsg({ kind: 'ok', text: 'Line added.' });
    setRow((x) => ({ ...blank(), poLine: x.poLine, vendor: x.vendor }));
    reload();
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.qty} x ${m.description || m.task} from ${m.vendor || 'the vendor'}?`)) return;
    const r = await deleteMaterial(projectId, m.id);
    if (!r.ok) {
      setMsg({ kind: 'error', text: (r.body && r.body.error) || 'Could not remove the line.' });
      return;
    }
    reload();
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <Figure label="Materials sold" value={money(t.materialsSold)} tone="#E8F5E9" />
        <Figure label="Materials cost" value={money(t.materialsCost)} />
        {seesCosts && <Figure label="Margin" value={pct(t.materialsMargin)} tone="#F3E5F5" />}
        <Figure label="Lines" value={String(lines.length)} />
      </div>
      {msg && (
        <div
          role={msg.kind === 'error' ? 'alert' : 'status'}
          style={{ marginBottom: 10, fontSize: 12, fontWeight: 600, color: msg.kind === 'error' ? '#B71C1C' : GREEN }}
        >
          {msg.text}
        </div>
      )}
      {editable && (
        <Section title="Add a line">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <Field label="Date">
              <TextInput type="date" aria-label="Material date" value={row.date} onChange={set('date')} />
            </Field>
            <Field label="PO line">
              <TextInput aria-label="Material PO line" list="mat-po" value={row.poLine} onChange={set('poLine')} />
              <datalist id="mat-po">
                {(data.poLines || []).map((l) => (
                  <option key={l.label} value={l.label} />
                ))}
              </datalist>
            </Field>
            <Field label="PO status">
              <TextInput aria-label="Material PO status" value={row.poStatus} onChange={set('poStatus')} />
            </Field>
            <Field label="Vendor">
              <TextInput aria-label="Material vendor" value={row.vendor} onChange={set('vendor')} />
            </Field>
            <Field label="Task">
              <SelectInput aria-label="Material task" value={row.task} options={tasks} onChange={set('task')} />
            </Field>
            <Field label="Description">
              <TextInput aria-label="Material description" value={row.description} onChange={set('description')} />
            </Field>
            <Field label="Quantity">
              <NumInput aria-label="Material quantity" value={row.qty} onChange={set('qty')} />
            </Field>
            <Field label="Unit sell ($)">
              <NumInput aria-label="Material unit sell" value={row.unitSell} onChange={set('unitSell')} />
            </Field>
            <Field label="Unit cost ($)">
              <NumInput aria-label="Material unit cost" value={row.unitCost} onChange={set('unitCost')} />
            </Field>
            <Field label="Vendor invoice #">
              <TextInput aria-label="Material vendor invoice" value={row.vendorInvoiceNo} onChange={set('vendorInvoiceNo')} />
            </Field>
            <Field label="Vendor amount ($)">
              <NumInput aria-label="Material vendor amount" value={row.vendorInvoiceAmount} onChange={set('vendorInvoiceAmount')} />
            </Field>
            <div style={{ alignSelf: 'end' }}>
              <Btn primary disabled={busy} onClick={add}>
                Add line
              </Btn>
            </div>
          </div>
        </Section>
      )}
      <Section title={`Equipment & Materials (${lines.length})`}>
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="Equipment and materials" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[
                  'Date',
                  'PO line',
                  'Status',
                  'Vendor',
                  'Task',
                  'Description',
                  'Qty',
                  'Unit sell',
                  'Unit cost',
                  'Sold',
                  'Cost',
                  'Vendor inv.',
                  '',
                ].map((x) => (
                  <th key={x} style={th}>
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((m) => (
                <tr key={m.id}>
                  <td style={{ ...td, fontSize: 11.5, whiteSpace: 'nowrap' }}>{m.date}</td>
                  <td style={{ ...td, fontSize: 11 }}>{m.poLine}</td>
                  <td style={{ ...td, fontSize: 11 }}>{m.poStatus}</td>
                  <td style={{ ...td, fontSize: 11.5 }}>{m.vendor}</td>
                  <td style={{ ...td, fontSize: 11 }}>{m.task}</td>
                  <td style={{ ...td, fontSize: 11.5 }}>{m.description}</td>
                  <Num v={m.qty} />
                  <Num v={money(m.unitSell)} />
                  <Num v={money(m.unitCost)} />
                  <Num v={money(m.sellTotal)} strong />
                  <Num v={money(m.costTotal)} />
                  <td style={{ ...td, fontSize: 10.5, color: MUTED }}>
                    {m.vendorInvoiceNo}
                    {m.vendorInvoiceAmount ? ` ${money(m.vendorInvoiceAmount)}` : ''}
                  </td>
                  <td style={{ ...td, width: 30 }}>
                    {editable && (
                      <Btn small danger aria-label={`Remove material ${m.id}`} onClick={() => remove(m)}>
                        x
                      </Btn>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!lines.length && (
          <Empty
            text={
              editable
                ? 'No equipment or materials on this job yet - add the first line above (vendor, quantity, unit sell and unit cost).'
                : 'No equipment or materials on this job yet.'
            }
          />
        )}
      </Section>
    </div>
  );
}
